import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { Chip } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { formatClock } from '@/utils/format';

export interface ExamTimerProps {
  /** ISO time the exam expires (server time). null = untimed. */
  expiresAt: string | null;
  /** Offset between server and client clocks in ms (server - client). */
  clockOffsetMs?: number;
  startedAt: string;
  onExpire?: () => void;
}

/**
 * Persistent countdown. Uses the server's expiry so refreshing / switching
 * devices cannot extend the time. Announces the last minutes to screen readers.
 */
export function ExamTimer({ expiresAt, clockOffsetMs = 0, startedAt, onExpire }: ExamTimerProps) {
  const now = () => Date.now() + clockOffsetMs;
  const compute = () =>
    expiresAt ? Math.max(0, (new Date(expiresAt).getTime() - now()) / 1000) : Math.max(0, (now() - new Date(startedAt).getTime()) / 1000);
  const [seconds, setSeconds] = useState(compute);
  const fired = useRef(false);

  useEffect(() => {
    const id = window.setInterval(() => {
      const s = compute();
      setSeconds(s);
      if (expiresAt && s <= 0 && !fired.current) {
        fired.current = true;
        onExpire?.();
      }
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt, clockOffsetMs, startedAt]);

  const low = !!expiresAt && seconds <= 300;
  const critical = !!expiresAt && seconds <= 60;
  return (
    <Chip
      icon={<TimerOutlinedIcon />}
      label={formatClock(seconds)}
      color={critical ? 'error' : low ? 'warning' : 'default'}
      variant={low ? 'filled' : 'outlined'}
      role="timer"
      aria-live={low ? 'polite' : 'off'}
      aria-label={expiresAt ? `Time remaining ${formatClock(seconds)}` : `Time elapsed ${formatClock(seconds)}`}
      sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: '1rem', height: 36 }}
    />
  );
}
